import { useEmbarcacaoBaseTecnica } from "@/modules/embarcacoes";
import type { EmbarcacaoBaseTecnica, EmbarcacaoBaseTecnicaField } from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import { formatCnpj } from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";

export const BASE_TECNICA_ID = "base-tecnica";
export const BASE_TECNICA_TITLE = "Base Técnica";
export const BASE_TECNICA_LOADING_MESSAGE = "Carregando a Base Técnica da embarcação...";

/** Padrão global de estado vazio do Hub para a seção sem informações (AC5). */
const BASE_TECNICA_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação de Base Técnica disponível",
  description: "Assim que houver dados técnicos para esta embarcação, eles aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-base-tecnica";

interface BaseTecnicaFieldDefinition {
  readonly key: EmbarcacaoBaseTecnicaField;
  readonly label: string;
  /** Tratamento de apresentação do valor; sem ele o valor vai como recebido. */
  readonly format?: (value: string) => string;
  /** Campo que abre linha no grid, para reproduzir o pareamento do protótipo. */
  readonly startsRow?: boolean;
}

/**
 * Os 27 campos da seção, na ordem e nas colunas do protótipo (AC3). O grid os
 * distribui em duas colunas, preenchendo a linha da esquerda para a direita.
 *
 * O protótipo mantém Grupo e CNPJ para Faturamento no mesmo par, o que deixa
 * IMO sozinho na coluna da esquerda: por isso Grupo abre linha. Daí em diante o
 * preenchimento corrido já reproduz as colunas desenhadas, par a par.
 */
export const BASE_TECNICA_FIELDS: BaseTecnicaFieldDefinition[] = [
  { key: "nome", label: "Embarcação" },
  { key: "nomeAntigo", label: "Nome antigo" },
  { key: "nomeAbreviado", label: "Nome Abreviado" },
  { key: "acronimo", label: "Acrônimo" },
  { key: "tipo", label: "Tipo" },
  { key: "empresaOrigem", label: "Empresa Origem (Ocean/CBO)" },
  { key: "mmsi", label: "MMSI" },
  { key: "vesselSignalLetters", label: "Vessel Signal Letters" },
  { key: "imo", label: "IMO" },
  { key: "grupo", label: "Grupo", startsRow: true },
  { key: "cnpjFaturamento", label: "CNPJ para Faturamento", format: formatCnpj },
  // O protótipo qualifica os dois campos do armador; o AC3 os cita abreviados.
  { key: "armador", label: "Nome do Armador" },
  { key: "cnpj", label: "CNPJ do Armador", format: formatCnpj },
  { key: "centroCusto", label: "Centro de Custo" },
  { key: "sisterVessels", label: "Sister Vessels" },
  { key: "possuiReb", label: "Possui REB?" },
  { key: "bandeiraSuspensa", label: "Bandeira Suspensa" },
  { key: "bandeira", label: "Bandeira" },
  { key: "paisConstrucao", label: "País de Construção" },
  { key: "estaleiroConstrucao", label: "Estaleiro de Construção" },
  { key: "anoBatimentoQuilha", label: "Ano do Batimento de Quilha" },
  { key: "anoFinalConstrucao", label: "Ano Final da Construção" },
  { key: "idade", label: "Idade" },
  { key: "anoModernizacao", label: "Ano Modernização" },
  { key: "sistemaGerencial", label: "Sistema Gerencial" },
  { key: "totalPessoas", label: "Quantidade total de Pessoas (POB)" },
  { key: "situacaoEmbarcacao", label: "Situação da Embarcação (Ativo/Inativo)" },
];

/** Valor do campo pronto para a tela; `null` segue como campo sem preenchimento. */
function fieldValue(
  baseTecnica: EmbarcacaoBaseTecnica,
  { key, format }: BaseTecnicaFieldDefinition,
): string | null {
  const value = baseTecnica[key];
  if (value === null) return null;

  return format ? format(value) : value;
}

/**
 * Seção Base Técnica do detalhamento da embarcação (US-1791).
 *
 * Exclusivamente de leitura (RN003): nenhum botão ou campo de cadastro, edição
 * ou exclusão. Os valores são apresentados como recebidos da fonte, com os
 * únicos tratamentos previstos — máscara de CNPJ e "Campo sem preenchimento"
 * para valor ausente (AC4).
 */
export function BaseTecnicaSection({
  embarcacaoId,
  onReauthenticate,
}: EmbarcacaoDetailSectionProps) {
  const { status, baseTecnica, errorMessage, reload } = useEmbarcacaoBaseTecnica(embarcacaoId);

  return (
    <DetailSection
      id={BASE_TECNICA_ID}
      title={BASE_TECNICA_TITLE}
      // Só há data de atualização a exibir quando a seção tem dados.
      ultimaAtualizacao={baseTecnica ? baseTecnica.ultimaAtualizacao : undefined}
      testId={TEST_ID}
    >
      {baseTecnica ? (
        <DetailSectionFields testId={`${TEST_ID}-fields`}>
          {BASE_TECNICA_FIELDS.map((field) => (
            <DetailField
              key={field.key}
              label={field.label}
              value={fieldValue(baseTecnica, field)}
              startsRow={field.startsRow}
              testId={`${TEST_ID}-${field.key}`}
            />
          ))}
        </DetailSectionFields>
      ) : (
        <DetailSectionState
          // `success` sempre traz os campos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={BASE_TECNICA_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={BASE_TECNICA_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
